import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import AppHeader from "@/components/AppHeader";
import RoundFeedbackModal from "@/components/RoundFeedbackModal";
import UpgradeModal from "@/components/UpgradeModal";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import useActiveStage from "@/hooks/useActiveStage";
import useBallotActions from "@/hooks/useBallotActions";
import useBallotState from "@/hooks/useBallotState";
import useRoundLock from "@/hooks/useRoundLock";
import useSpeechTimer from "@/hooks/useSpeechTimer";
import useWorkspaceAccess from "@/hooks/useWorkspaceAccess";

export default function JudgeWorkspace() {
  const navigate = useNavigate();
  const { isPro, user } = useWorkspaceAccess();
  const {
    addCustomCriterion,
    ballot,
    resetBallot,
    removeCustomCriterion,
    setBallot,
    updateDetails,
    updateCustomCriterion,
    updateNotes,
    updateScore,
    updateSpeaker,
    isHydrated,
  } = useBallotState(user?.user_id);
  const {
    activePreset,
    isRunning,
    resetTimer,
    seconds,
    selectPreset,
    setIsRunning,
  } = useSpeechTimer();
  const [savedToAccount, setSavedToAccount] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [roundVersion, setRoundVersion] = useState(0);
  const closeFeedbackModal = useCallback(() => setShowFeedbackModal(false), []);
  const openFeedbackModal = useCallback(() => setShowFeedbackModal(true), []);
  const handleRoundReset = useCallback(() => {
    setRoundVersion((version) => version + 1);
    openFeedbackModal();
  }, [openFeedbackModal]);
  const { activeStage, setActiveStage } = useActiveStage(user?.user_id);
  const {
    endRoundLock,
    isRoundLockHydrated,
    isRoundEnded,
    isRoundStarted,
    resetRoundLock,
    startRoundLock,
  } = useRoundLock(user?.user_id);
  useEffect(() => {
    const openUpgrade = () => setShowUpgradeModal(true);
    window.addEventListener("pf-upgrade-needed", openUpgrade);
    return () => window.removeEventListener("pf-upgrade-needed", openUpgrade);
  }, []);

  const teams = useMemo(
    () => ({
      pro: ballot.details.proTeam || "Pro",
      con: ballot.details.conTeam || "Con",
    }),
    [ballot.details],
  );
  const resolvedBestSpeakerId = useMemo(() => {
    if (ballot.bestSpeakerId !== "auto") return ballot.bestSpeakerId;
    return (
      [...ballot.speakers].sort((a, b) => b.points - a.points)[0]?.id || ""
    );
  }, [ballot.bestSpeakerId, ballot.speakers]);
  const { exportBallot, exportPdf, newRound, saveBallot } = useBallotActions({
    ballot,
    isPro,
    navigate,
    onRoundReset: handleRoundReset,
    onRoundUnlock: resetRoundLock,
    resetBallot,
    resetTimer,
    resolvedBestSpeakerId,
    setActiveStage,
    setSavedToAccount,
    teams,
  });
  const updateBestSpeaker = useCallback(
    (bestSpeakerId) => {
      setBallot((current) => ({ ...current, bestSpeakerId }));
    },
    [setBallot],
  );
  const toggleTimer = useCallback(() => {
    const toggle = async () => {
      if (!isRunning) {
        const started = await startRoundLock();
        if (!started) {
          toast.error("The round could not be started. Please try again.");
          return;
        }
      }
      setIsRunning((current) => !current);
    };
    void toggle();
  }, [isRunning, setIsRunning, startRoundLock]);
  const endRound = useCallback(() => {
    const end = async () => {
      setIsRunning(false);
      const ended = await endRoundLock();
      if (!ended) {
        toast.error("The round could not be ended. Please try again.");
      }
    };
    void end();
  }, [endRoundLock, setIsRunning]);
  if (!user || !isHydrated || !isRoundLockHydrated) {
    return (
      <div className="judge-app" data-testid="workspace-loading">
        Loading your workspace…
      </div>
    );
  }
  return (
    <div className="judge-app" data-testid="judge-workspace">
      <RoundFeedbackModal
        onClose={closeFeedbackModal}
        open={showFeedbackModal}
      />
      <UpgradeModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      <AppHeader onNewRound={newRound} isPro={isPro} isOwner={user?.is_owner} />
      <WorkspaceLayout
        activePreset={activePreset}
        activeStage={activeStage}
        ballot={ballot}
        exportBallot={exportBallot}
        exportPdf={exportPdf}
        isPro={isPro}
        isRoundEnded={isRoundEnded}
        isRoundStarted={isRoundStarted}
        isRunning={isRunning}
        onBestSpeakerChange={updateBestSpeaker}
        onAddCriterion={addCustomCriterion}
        onCustomCriterionChange={updateCustomCriterion}
        onNoteChange={updateNotes}
        onEndRound={endRound}
        onPreset={selectPreset}
        onRemoveCriterion={removeCustomCriterion}
        onScoreChange={updateScore}
        onSpeakerChange={updateSpeaker}
        onStageChange={setActiveStage}
        onTimerReset={resetTimer}
        onTimerToggle={toggleTimer}
        onUpdateDetails={updateDetails}
        saveBallot={saveBallot}
        roundVersion={roundVersion}
        savedToAccount={savedToAccount}
        seconds={seconds}
        teams={teams}
      />
    </div>
  );
}
